<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useHead } from '@unhead/vue'

import HomePageFooter from '../components/HomePageFooter.vue'
import HomePageHeader from '../components/HomePageHeader.vue'
import LandingBackground from '../components/LandingBackground.vue'
import MessageDialog from '../components/MessageDialog.vue'
import TaskStatusTag from '../components/TaskStatusTag.vue'
import TaskSubmitProgressDialog from '../components/TaskSubmitProgressDialog.vue'
import IconClose from '../components/icons/IconClose.vue'
import IconChevronLeft from '../components/icons/IconChevronLeft.vue'
import IconChevronRight from '../components/icons/IconChevronRight.vue'
import IconDownload from '../components/icons/IconDownload.vue'
import IconPlay from '../components/icons/IconPlay.vue'
import IconRefresh from '../components/icons/IconRefresh.vue'
import IconTrash from '../components/icons/IconTrash.vue'
import { brand } from '../config/brand'
import { videoWatermarkRemovalSeo } from '../content/videoWatermarkRemovalSeo'
import { apiRequest, assertOk, isAuthRequiredError, AUTH_EXPIRED_EVENT, AUTH_EXPIRED_STORAGE_KEY } from '../lib/apiClient'
import { absoluteUrl, organizationJsonLd, webApplicationJsonLd } from '../lib/structuredData'
import { getTaskThumbFromCache, setTaskThumbToCache, removeTaskThumbCache } from '../lib/taskThumbCache'
import { TASK_STATUS, isActiveTaskStatus, normalizeTaskStatus } from '../lib/taskStatus'
import { estimateTaskMinutes } from '../lib/taskEstimate'
import { TASK_TYPES } from '../workbench/taskCatalog'
import type { LandingSectionKey } from '../lib/landingTypes'
import { getUploadUrl, putFile } from '../lib/upload'
import { useAuthStore } from '../stores/auth'
import { useLoginModalStore } from '../stores/loginModal'
import { usePaymentModalStore } from '../stores/paymentModal'
import { useTaskStore } from '../stores/task'
import { useVideoTaskListStore, type VideoRect as ApiVideoRect, type VideoRemoveMethod } from '../stores/videoTasks'

type StartTaskResponse = { task_id?: string; status?: string; credits_required?: number }
type VideoRemoveResponse = { task_id?: string; status?: string; credits_required?: number }
type LandingRecommendation = { icon: string; name: string; desc: string; href: string }

const auth = useAuthStore()
const taskStore = useTaskStore()
const videoTasks = useVideoTaskListStore()
const loginModal = useLoginModalStore()
const paymentModal = usePaymentModalStore()

const filePicker = ref<HTMLInputElement | null>(null)
const videoEl = ref<HTMLVideoElement | null>(null)
const previewWrapEl = ref<HTMLElement | null>(null)

const videoFile = ref<File | null>(null)
const videoUrl = ref('')
const demoPlayed = ref(false)
const isDemoVideo = computed(() => videoUrl.value === props.demoVideoUrl && !videoFile.value)
const isMobileViewport = ref(false)
const videoW = ref(0)
const videoH = ref(0)
const videoDuration = ref(0)
const originObjectKey = ref('')
const uploadBusy = ref(false)

function syncMobileViewport() {
  const w = Number(window.innerWidth || 0) || 0
  isMobileViewport.value = w > 0 && w < 640
}

const method = ref<VideoRemoveMethod>('sel_area')
type UiRect = { x: number; y: number; w: number; h: number }
const rect = ref<UiRect>({ x: 0, y: 0, w: 0, h: 0 })

const dragging = ref(false)
const resizing = ref<null | 'nw' | 'ne' | 'sw' | 'se'>(null)
const pointerStart = ref({ x: 0, y: 0 })
const rectStart = ref<UiRect>({ x: 0, y: 0, w: 0, h: 0 })

const siteUrl = (
  (import.meta.env.VITE_SITE_URL as string | undefined) ||
  'https://www.wuhenai.com'
).replace(/\/+$/, '')
function runtimeBasePath() {
  const v = (import.meta.env.BASE_URL as string | undefined) || '/'
  return v === '/' ? '/' : v.replace(/\/?$/, '/')
}
function withBase(p: string) {
  const s = String(p || '').trim()
  if (!s) return ''
  if (/^https?:\/\//.test(s)) return s
  return `${runtimeBasePath()}${s.replace(/^\//, '')}`
}

const tutorialIconMaskStyle = computed(() => {
  const href = withBase('/assets/tutorial-icon.svg')
  return {
    backgroundColor: 'currentColor',
    WebkitMask: `url("${href}") no-repeat center / contain`,
    mask: `url("${href}") no-repeat center / contain`,
  } as any
})

function openHowTo() {
  const url = 'https://suiyu-network.feishu.cn/wiki/FtpvwylhQiqWNAklbeMcWls1n7b#share-AO1gdmA5NoShkvxK1P8cBvnunyf'
  try {
    const w = window.open(url, '_blank', 'noopener,noreferrer')
    if (w) w.opener = null
  } catch {
    window.open(url, '_blank')
  }
}

function whereImageSrc(idx: number) {
  const i = Math.min(12, Math.max(1, Math.floor(Number(idx || 0)) + 1))
  return withBase(`/assets/where-${i}.jpg`)
}

function whyNeedImageSrc(idx: number) {
  const list = Array.isArray(props.benefitsImages) ? props.benefitsImages : []
  const i = Math.max(0, Math.floor(Number(idx || 0)))
  const pick = list.length ? String(list[i] || list[i % list.length] || '').trim() : ''
  if (pick) return withBase(pick)
  const j = (Math.abs(Number(idx || 0)) % 2) + 1
  return withBase(`/assets/why-need-${j}.jpg`)
}

const whyChooseItems = computed(() => {
  const list = Array.isArray(props.whyChooseImages) ? props.whyChooseImages : []
  const items = Array.isArray(landingSeo.whyItems) ? landingSeo.whyItems : []
  return items.map((w: any, idx: number) => {
    const img = list.length ? String(list[idx] || list[idx % list.length] || '').trim() : ''
    if (!img) return w
    return { ...w, image: img }
  })
})

const featureItems = computed(() => {
  const list = Array.isArray(props.featuresImages) ? props.featuresImages : []
  const items = Array.isArray(landingSeo.featuresItems) ? landingSeo.featuresItems : []
  return items.map((f: any, idx: number) => {
    const img = list.length ? String(list[idx] || list[idx % list.length] || '').trim() : ''
    if (!img) return f
    return { ...f, image: img }
  })
})

function defaultVideoThumbDataUrl() {
  return withBase('/assets/default-video-thumb.svg')
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve) => {
    try {
      const reader = new FileReader()
      reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '')
      reader.onerror = () => resolve('')
      reader.readAsDataURL(blob)
    } catch {
      resolve('')
    }
  })
}

async function downloadThumbToCache(taskId: string, url: string) {
  const id = String(taskId || '').trim()
  const u = String(url || '').trim()
  if (!id || !u) return ''
  const cached = getTaskThumbFromCache(id)
  if (cached) return cached
  try {
    const res = await fetch(u, { method: 'GET' })
    if (!res.ok) return ''
    const blob = await res.blob()
    const dataUrl = await blobToDataUrl(blob)
    if (!dataUrl) return ''
    setTaskThumbToCache(id, dataUrl)
    return dataUrl
  } catch {
    return ''
  }
}

async function warmTaskThumbsAfterListLoaded() {
  const list = videoTasks.items.filter((x: any) => {
    const tt = String(x?.task_type || '').trim()
    if (tt && tt !== TASK_TYPES.removeWatermark_vid) return false
    const subLen = Math.max(0, Math.round(Number(x?.sub_task_len || 0)) || 0)
    return subLen === 0
  })
  for (const t of list) {
    const taskId = String(t?.task_id || '').trim()
    if (!taskId) continue
    const cached = getTaskThumbFromCache(taskId)
    if (cached) {
      videoTasks.upsert({ task_id: taskId, thumb_data_url: cached } as any)
      continue
    }
    const local = String(t?.thumb_data_url || '').trim()
    if (local.startsWith('data:image/')) {
      setTaskThumbToCache(taskId, local)
      videoTasks.upsert({ task_id: taskId, thumb_data_url: local } as any)
      continue
    }
    const remote = String(t?.task_thumb || '').trim()
    if (!remote) continue
    const next = await downloadThumbToCache(taskId, remote)
    if (!next) continue
    videoTasks.upsert({ task_id: taskId, thumb_data_url: next } as any)
  }
}

function taskThumbSrc(t: any) {
  const id = String(t?.task_id || '').trim()
  if (!id) return ''
  const cached = getTaskThumbFromCache(id)
  if (cached) return cached
  const local = String(t?.thumb_data_url || '').trim()
  if (local.startsWith('data:image/')) {
    setTaskThumbToCache(id, local)
    return local
  }
  return ''
}

function formatMmssFromSeconds(v: unknown) {
  const s = Math.max(0, Math.round(Number(v) || 0))
  const mm = String(Math.floor(s / 60)).padStart(2, '0')
  const ss = String(s % 60).padStart(2, '0')
  return `${mm}:${ss}`
}

function taskMetaText(t: any) {
  const meteringText = formatMmssFromSeconds(t?.metering)
  const desc = String(t?.description || '').trim() || '—'
  const timeText = formatDate(Number(t?.created_at || 0))
  return `${meteringText} ｜ ${desc} ｜ ${timeText}`
}

function estimateMinutes(t: any) {
  return estimateTaskMinutes({ taskType: TASK_TYPES.removeWatermark_vid, meteringSeconds: t?.metering })
}

function toProgressInt(v: unknown) {
  const n = Math.round(Number(v || 0))
  return Number.isFinite(n) ? Math.max(0, n) : 0
}

function queueHintText(t: any) {
  const p = toProgressInt(t?.progress)
  if (p <= 0) return '即将开始处理'
  return `前面还有${p}个任务在处理`
}

function processingHintText(t: any) {
  const p = toProgressInt(t?.progress)
  if (p > 0) return `处理进度：${p}%`
  return `预计${estimateMinutes(t)}分钟内完成`
}

const props = withDefaults(
  defineProps<{
    landingPageId?: string
    landingSeo?: any
    canonicalPath?: string
    breadcrumbName?: string
    howToTitle?: string
    seoTitle?: string
    seoDescription?: string
    benefitsImages?: string[]
    featuresImages?: string[]
    whyChooseImages?: string[]
    enabledSections?: Partial<Record<LandingSectionKey, boolean>>
    demoVideoUrl?: string
    defaultInvitationCode?: string
    recommendations?: LandingRecommendation[]
    hreflang?: Record<string, string>
    embed?: boolean
  }>(),
  {
    landingPageId: '',
    canonicalPath: '/video-watermark-removal',
    breadcrumbName: '视频去水印',
    howToTitle: undefined,
    seoTitle: undefined,
    seoDescription: undefined,
    benefitsImages: undefined,
    featuresImages: undefined,
    whyChooseImages: undefined,
    enabledSections: undefined,
    demoVideoUrl: 'https://batchwith.oss-cn-hangzhou.aliyuncs.com/demo/watermark-remover-demo.mp4',
    defaultInvitationCode: 'OF3A7K',
    recommendations: undefined,
    hreflang: undefined,
    embed: false,
  },
)

const sectionEnabled = (key: LandingSectionKey) => props.enabledSections?.[key] !== false
const recommendations = computed(() => {
  const list = Array.isArray(props.recommendations) ? props.recommendations : []
  return list.slice(0, 4)
})

function resolveRecommendHref(href: string) {
  const s = String(href || '').trim()
  if (!s) return ''
  if (s.startsWith('#')) return s
  if (/^https?:\/\//.test(s)) return s
  const path = s.startsWith('/') ? s : `/${s}`
  if (runtimeBasePath() !== '/') return `${siteUrl}${path}`
  return path
}

const landingSeo = (props.landingSeo || videoWatermarkRemovalSeo) as any
const canonicalPath = computed(() => {
  const p = String(props.canonicalPath || '').trim() || '/video-watermark-removal'
  return p.startsWith('/') ? p : `/${p}`
})
const canonical = computed(() => `${siteUrl}${canonicalPath.value}`)

const isPrimaryLanding = computed(() => {
  if (props.embed) return false
  const id = String(props.landingPageId || '').trim()
  return id === 'video-watermark-removal'
})
const htmlLang = computed(() => {
  const m = props.hreflang || {}
  const k = Object.keys(m).find((x) => x && x !== 'x-default') || ''
  return k || 'zh-CN'
})
const hreflangLinks = computed(() => {
  const m = props.hreflang || {}
  const out: Array<{ rel: string; hreflang?: string; href: string }> = []
  for (const [lang, path] of Object.entries(m)) {
    const p = String(path || '').trim()
    if (!p) continue
    const href = /^https?:\/\//.test(p) ? p : `${siteUrl}${p.startsWith('/') ? p : `/${p}`}`
    out.push({ rel: 'alternate', hreflang: lang, href })
  }
  return out
})
const description = computed(() => {
  const preset = String(props.seoDescription || '').trim()
  if (preset) return preset
  const steps = landingSeo.howto || []
  if (steps.length >= 2) return `${steps[0].desc} ${steps[1].desc}`.trim()
  return '上传视频，选择框选或全屏去水印方式，提交处理后即可下载无水印视频。'
})
const pageTitle = computed(() => {
  const preset = String(props.seoTitle || '').trim()
  if (preset) return preset
  return landingSeo.h1 ? `${landingSeo.h1} - ${brand.productName}` : brand.productName
})
const socialImage = computed(() => absoluteUrl(siteUrl, brand.ogImagePath))

const faqJsonLd = computed(() => {
  if (!sectionEnabled('faq')) return ''
  const faq = Array.isArray(landingSeo.faq) ? landingSeo.faq : []
  if (!faq.length) return ''
  return JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faq.map((x: any) => ({
      '@type': 'Question',
      name: String(x.question || ''),
      acceptedAnswer: { '@type': 'Answer', text: String(x.answer || '') },
    })),
  })
})

const orgJsonLd = computed(() => {
  return organizationJsonLd(siteUrl, absoluteUrl(siteUrl, brand.logoPath))
})

const webAppJsonLd = computed(() => {
  if (!isPrimaryLanding.value) return ''
  return webApplicationJsonLd({
    siteUrl,
    name: '无痕AI视频去水印',
    description: `${brand.productName}是一款智能图片及视频批处理平台，支持视频去水印等能力。`,
    inLanguage: htmlLang.value,
  })
})

const webPageJsonLd = computed(() => {
  return JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${canonical.value}#webpage`,
    url: canonical.value,
    name: pageTitle.value,
    isPartOf: { '@id': `${siteUrl}#website` },
  })
})

const breadcrumbJsonLd = computed(() => {
  return JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: siteUrl },
      { '@type': 'ListItem', position: 2, name: props.breadcrumbName, item: canonical.value },
    ],
  })
})

const jsonLdScripts = computed(() => {
  const out: any[] = []
  const add = (key: string, json: string) => {
    if (!json) return
    out.push({ key, type: 'application/ld+json', textContent: json })
  }
  if (isPrimaryLanding.value) {
    add('jsonld-org', orgJsonLd.value)
    add('jsonld-webapp', webAppJsonLd.value)
  }
  add('jsonld-webpage', webPageJsonLd.value)
  add('jsonld-breadcrumb', breadcrumbJsonLd.value)
  if (isPrimaryLanding.value) add('jsonld-faq', faqJsonLd.value)
  return out
})

if (!props.embed) {
  useHead(() => ({
    htmlAttrs: { lang: htmlLang.value },
    title: pageTitle.value,
    meta: [
      { name: 'description', content: description.value },
      { property: 'og:title', content: pageTitle.value },
      { property: 'og:description', content: description.value },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: canonical.value },
      { property: 'og:image', content: socialImage.value },
      { property: 'og:site_name', content: brand.productName },
      { property: 'og:locale', content: htmlLang.value },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: pageTitle.value },
      { name: 'twitter:description', content: description.value },
      { name: 'twitter:image', content: socialImage.value },
    ],
    link: [{ rel: 'canonical', href: canonical.value }, ...hreflangLinks.value],
    script: jsonLdScripts.value,
  }))
}

const modalOpen = ref(false)
const modalTitle = ref('提示')
const modalPrimaryText = ref('我知道了')
const modalBodyHtml = ref('')
const modalPrimaryAction = ref<null | (() => void)>(null)

const warnOpen = ref(false)
const warnText = ref('')
function openWarn(msg: string) {
  warnText.value = String(msg || '').trim()
  warnOpen.value = true
}
function closeWarn() {
  warnOpen.value = false
  warnText.value = ''
}

type SubmitStep = 'upload' | 'create' | 'start'
type SubmitStage = 'uploading' | 'creating' | 'starting' | 'error'

const submitOpen = ref(false)
const submitStage = ref<SubmitStage>('uploading')
const submitUploadPct = ref(0)
const submitErrorText = ref('')
const submitErrorStep = ref<SubmitStep>('upload')

function openSubmitDialog() {
  submitOpen.value = true
  submitStage.value = 'uploading'
  submitUploadPct.value = 0
  submitErrorText.value = ''
  submitErrorStep.value = 'upload'
}

function closeSubmitDialog() {
  submitOpen.value = false
}

function setSubmitError(step: SubmitStep, message: string) {
  submitStage.value = 'error'
  submitErrorStep.value = step
  submitErrorText.value = message
}

function handleAuthExpiredDuringSubmit() {
  closeSubmitDialog()
  try {
    auth.clearToken()
  } catch {}
  loginModal.show({ tab: 'sms', defaultInvitationCode: props.defaultInvitationCode })
}

function onAuthExpired() {
  handleAuthExpiredDuringSubmit()
}

function openModal(title: string, primaryText: string, bodyHtml: string, action?: () => void) {
  modalTitle.value = title
  modalPrimaryText.value = primaryText
  modalBodyHtml.value = bodyHtml
  modalPrimaryAction.value = action || null
  modalOpen.value = true
}

function closeModal() {
  modalOpen.value = false
  modalPrimaryAction.value = null
}

type RechargeEntry = 'manual' | 'insufficient'
async function openRechargeModal(entry: RechargeEntry, creditsRequired: number, tipText?: string) {
  if (!ensureLoggedIn()) return
  try {
    await paymentModal.pay({ entry, creditsRequired, tipText })
  } catch {}
}

function ensureLoggedIn(): boolean {
  if (auth.token) return true

  loginModal.show({ tab: 'sms', defaultInvitationCode: props.defaultInvitationCode })

  return false
}

function validateVideoMeta(): '' | 'missing_meta' | 'too_short' | 'too_long' | 'too_large_res' {
  if (!videoW.value || !videoH.value) return 'missing_meta'
  if (videoDuration.value < 3) return 'too_short'
  if (videoDuration.value > 30 * 60) return 'too_long'
  const maxSide = Math.max(videoW.value, videoH.value)
  const minSide = Math.min(videoW.value, videoH.value)
  if (maxSide > 1968 || minSide > 1088) return 'too_large_res'
  return ''
}

function revokeVideoUrl() {
  if (videoUrl.value && videoUrl.value.startsWith('blob:')) {
    try {
      URL.revokeObjectURL(videoUrl.value)
    } catch {}
  }
  videoUrl.value = ''
}

function resetVideo() {
  revokeVideoUrl()
  videoFile.value = null
  videoW.value = 0
  videoH.value = 0
  videoDuration.value = 0
  originObjectKey.value = ''
  rect.value = { x: 0, y: 0, w: 0, h: 0 }
}

async function handleFileSelected(file: File) {
  if (!file.type.startsWith('video/')) {
    openModal('无法打开视频', '我知道了', `<div class="miniCard"><h4>校验失败</h4><p>不支持的文件类型</p></div>`)
    return
  }
  taskStore.stopPolling()
  taskStore.setCurrent(null)
  originObjectKey.value = ''
  videoFile.value = file
  revokeVideoUrl()
  videoUrl.value = URL.createObjectURL(file)
}

async function playDemoVideo() {
  if (demoPlayed.value) return
  if (videoFile.value) return
  if (videoUrl.value) return
  demoPlayed.value = true
  videoUrl.value = props.demoVideoUrl
  await nextTick()
  const v = videoEl.value
  if (!v) return
  try {
    v.muted = true
    await v.play()
  } catch {}
}

function onPickVideo() {
  filePicker.value?.click()
}

function onDropzoneDragOver(e: DragEvent) {
  e.preventDefault()
}

function onDropzoneDrop(e: DragEvent) {
  e.preventDefault()
  const f = e.dataTransfer?.files?.[0]
  if (f) void handleFileSelected(f)
}

function getPreviewRect() {
  const wrap = previewWrapEl.value
  if (!wrap) return null
  const w = Math.round(wrap.clientWidth)
  const h = Math.round(wrap.clientHeight)
  if (!w || !h) return null
  return { x: 0, y: 0, w, h }
}

function getVideoContentBox() {
  const pr = getPreviewRect()
  if (!pr) return null
  const vw = Math.round(videoW.value || 0)
  const vh = Math.round(videoH.value || 0)
  if (!vw || !vh) return { x: 0, y: 0, w: pr.w, h: pr.h }

  const videoRatio = vw / vh
  const boxRatio = pr.w / pr.h
  if (videoRatio > boxRatio) {
    const w = pr.w
    const h = Math.max(1, Math.round(pr.w / videoRatio))
    const y = Math.round((pr.h - h) / 2)
    return { x: 0, y, w, h }
  }
  const h = pr.h
  const w = Math.max(1, Math.round(pr.h * videoRatio))
  const x = Math.round((pr.w - w) / 2)
  return { x, y: 0, w, h }
}

function clampRect(r: UiRect, boundsW: number, boundsH: number) {
  const min = 24
  const maxArea = 480000
  let x = Math.round(r.x)
  let y = Math.round(r.y)
  let w = Math.round(r.w)
  let h = Math.round(r.h)
  w = Math.max(min, Math.min(w, boundsW))
  h = Math.max(min, Math.min(h, boundsH))
  const vw = Math.round(videoW.value || 0)
  const vh = Math.round(videoH.value || 0)
  if (vw > 0 && vh > 0 && boundsW > 0 && boundsH > 0) {
    const scaleX = vw / boundsW
    const scaleY = vh / boundsH
    const areaPx = () => Math.round(w * scaleX) * Math.round(h * scaleY)
    if (areaPx() > maxArea) {
      const areaFloat = Math.max(1, w * h * scaleX * scaleY)
      const k = Math.sqrt(maxArea / areaFloat)
      w = Math.max(min, Math.min(boundsW, Math.floor(w * k)))
      h = Math.max(min, Math.min(boundsH, Math.floor(h * k)))
      for (let i = 0; i < 24 && areaPx() > maxArea; i++) {
        const ow = Math.round(w * scaleX)
        const oh = Math.round(h * scaleY)
        if (ow >= oh) w = Math.max(min, w - 1)
        else h = Math.max(min, h - 1)
      }
    }
  }
  x = Math.max(0, Math.min(x, boundsW - w))
  y = Math.max(0, Math.min(y, boundsH - h))
  return { x, y, w, h }
}

function initRect() {
  const box = getVideoContentBox()
  if (!box) return
  const w = Math.round(box.w * 0.35)
  const h = Math.round(box.h * 0.18)
  const x = Math.round((box.w - w) * 0.6)
  const y = Math.round((box.h - h) * 0.15)
  rect.value = clampRect({ x, y, w, h }, box.w, box.h)
}

function onRectPointerDown(e: PointerEvent) {
  if (method.value !== 'sel_area' && method.value !== 'ant_area') return
  const box = getVideoContentBox()
  if (!box) return
  const target = e.target as HTMLElement | null
  const handle = target?.getAttribute('data-handle') as any
  if (handle === 'nw' || handle === 'ne' || handle === 'sw' || handle === 'se') resizing.value = handle
  else dragging.value = true
  pointerStart.value = { x: e.clientX, y: e.clientY }
  rectStart.value = { ...rect.value }
  try {
    ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
  } catch {}
}

function onRectPointerMove(e: PointerEvent) {
  const box = getVideoContentBox()
  if (!box) return
  if (!dragging.value && !resizing.value) return
  const dx = e.clientX - pointerStart.value.x
  const dy = e.clientY - pointerStart.value.y
  const base = rectStart.value
  let next = { ...base }
  if (dragging.value) {
    next.x = base.x + dx
    next.y = base.y + dy
  } else if (resizing.value) {
    const h = resizing.value
    if (h.includes('e')) next.w = base.w + dx
    if (h.includes('s')) next.h = base.h + dy
    if (h.includes('w')) {
      next.x = base.x + dx
      next.w = base.w - dx
    }
    if (h.includes('n')) {
      next.y = base.y + dy
      next.h = base.h - dy
    }
  }
  rect.value = clampRect(next, box.w, box.h)
}

function onRectPointerUp() {
  dragging.value = false
  resizing.value = null
}

const rectStyle = computed(() => {
  const box = getVideoContentBox()
  const ox = box?.x || 0
  const oy = box?.y || 0
  return {
    transform: `translate(${ox + rect.value.x}px, ${oy + rect.value.y}px)`,
    width: `${rect.value.w}px`,
    height: `${rect.value.h}px`,
  }
})

const rectAtLimit = computed(() => {
  if (method.value !== 'sel_area') return false
  if (!videoUrl.value) return false
  if (videoUrl.value === props.demoVideoUrl && !videoFile.value) return false
  const box = getVideoContentBox()
  if (!box) return false
  const boundsW = Math.round(box.w || 0)
  const boundsH = Math.round(box.h || 0)
  if (!boundsW || !boundsH) return false
  const vw = Math.round(videoW.value || 0)
  const vh = Math.round(videoH.value || 0)
  if (!vw || !vh) return false
  const maxArea = 480000
  const scaleX = vw / boundsW
  const scaleY = vh / boundsH
  const w = Math.round(Math.round(rect.value.w) * scaleX)
  const h = Math.round(Math.round(rect.value.h) * scaleY)
  const area = Math.max(0, w) * Math.max(0, h)
  return area >= maxArea - 3600
})

function rectToVideoPixels(): UiRect {
  const box = getVideoContentBox()
  if (!box || !videoW.value || !videoH.value) return { x: 0, y: 0, w: 0, h: 0 }
  const scaleX = videoW.value / box.w
  const scaleY = videoH.value / box.h
  return {
    x: Math.round(rect.value.x * scaleX),
    y: Math.round(rect.value.y * scaleY),
    w: Math.round(rect.value.w * scaleX),
    h: Math.round(rect.value.h * scaleY),
  }
}

function rectToApiPixels(): ApiVideoRect {
  const r = rectToVideoPixels()
  return { x1: r.x, y1: r.y, x2: r.x + r.w, y2: r.y + r.h }
}

async function createThumb(): Promise<string> {
  const vid = videoEl.value
  if (!vid) return ''
  const c = document.createElement('canvas')
  const w = Math.max(1, vid.videoWidth || videoW.value || 1)
  const h = Math.max(1, vid.videoHeight || videoH.value || 1)
  const scale = Math.min(1, 320 / w)
  c.width = Math.round(w * scale)
  c.height = Math.round(h * scale)
  const ctx = c.getContext('2d')
  if (!ctx) return ''
  ctx.drawImage(vid, 0, 0, c.width, c.height)
  return c.toDataURL('image/jpeg', 0.72)
}

async function submit() {
  
  if (!ensureLoggedIn()) return

  const file = videoFile.value
  if (!file) return

  const metaErr = validateVideoMeta()
  if (metaErr) {
    const dur = Number(videoDuration.value || 0)
    const durRounded = Math.round(dur * 10) / 10
    const durText = String(durRounded).replace(/\.0$/, '')
    if (metaErr === 'missing_meta') {
      openWarn('无法读取视频信息，为保证处理效果，请重新选择视频后再提交。')
      return
    }
    if (metaErr === 'too_short') {
      openWarn(`视频时长小于3秒，当前视频时长为${durText}秒。请重新选择视频。`)
      return
    }
    if (metaErr === 'too_long') {
      openWarn(`视频时长超过1800秒（30分钟），当前视频时长${durText}秒。建议将其裁切成小于30分钟的多段进行处理。`)
      return
    }
    if (metaErr === 'too_large_res') {
      const w = Math.round(Number(videoW.value || 0))
      const h = Math.round(Number(videoH.value || 0))
      openWarn(`视频分辨率超过1080P，当前分辨率为${w}×${h}，建议将视频压缩到1080P后再提交。`)
      return
    }
    openWarn('参数不合法，请重新选择视频后再提交。')
    return
  }


  openSubmitDialog()
  uploadBusy.value = true
  try {
    if (!originObjectKey.value) {
      let upload: any
      try {
        upload = await getUploadUrl(auth.token, file)
      } catch (e) {
        if (isAuthRequiredError(e)) {
          return
        }
        setSubmitError('upload', '视频上传失败，请重新提交。')
        return
      }

      submitStage.value = 'uploading'
      submitUploadPct.value = 0
      try {
        await putFile(upload, file, (p) => {
          submitUploadPct.value = p.percent
        })
      } catch {
        setSubmitError('upload', '视频上传失败，请重新提交。')
        return
      }
      originObjectKey.value = upload.object_key || ''
    }
    if (!originObjectKey.value) throw new Error('上传成功但缺少 object_key')
    submitUploadPct.value = 100

    submitStage.value = 'creating'
    const payload: any = { source_video: originObjectKey.value, method: method.value }
    const rawName = String(file.name || '').trim()
    const taskName = rawName.replace(/\.[^./\\]+$/, '')
    if (taskName) payload.task_name = taskName
    if ((method.value === 'sel_area' || method.value === 'ant_area') && rect.value.w > 0 && rect.value.h > 0) payload.rect = rectToApiPixels()

    let taskId = ''
    let createdStatus = 'created'
    let creditsRequired = 0
    try {
      const resp = await apiRequest<VideoRemoveResponse>('/video/removeWatermark', {
        method: 'POST',
        token: auth.token,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      } as any)
      const data = assertOk(resp, '提交失败') as any
      taskId = String(data.task_id || '').trim()
      createdStatus = String(data.status || 'created').toLowerCase()
      creditsRequired = typeof data.credits_required === 'number' ? data.credits_required : 0
      if (!taskId) throw new Error('empty task_id')
    } catch (e) {
      if (isAuthRequiredError(e)) return
      setSubmitError('create', '任务创建，请重新提交或联系客服处理。')
      return
    }

    taskStore.setCurrent({ task_id: taskId, status: 'created' } as any)
    const thumb = await createThumb()
    if (thumb) setTaskThumbToCache(taskId, thumb)
    videoTasks.upsert({
      task_id: taskId,
      title: file.name || '视频去水印',
      created_at: Date.now(),
      task_type: TASK_TYPES.removeWatermark_vid,
      status: createdStatus as any,
      source_video: originObjectKey.value,
      method: method.value,
      rect: method.value === 'sel_area' ? payload.rect : undefined,
      thumb_data_url: thumb || undefined,
    })
    try {
      await videoTasks.fetchRecent(auth.token, 1, TASK_TYPES.removeWatermark_vid, 0, 'ms', undefined, true, { authExpiredMode: 'silent' })
    } catch (e) {
      if (isAuthRequiredError(e)) videoTasks.reset()
    }

    submitStage.value = 'starting'
    let startResp: any
    try {
      startResp = await apiRequest<StartTaskResponse>('/task/start', {
        method: 'POST',
        token: auth.token,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ task_id: taskId }),
      } as any)
    } catch (e) {
      if (isAuthRequiredError(e)) return
      throw e
    }
    const startCode = Number(startResp.code)
    if (startCode === 3001 || startCode === 3002) {
      const required = typeof startResp.data?.credits_required === 'number' ? startResp.data.credits_required : creditsRequired
      closeSubmitDialog()
      void openRechargeModal('insufficient', required, String(startResp.message || '').trim())
      return
    }
    if (startCode !== 0) {
      setSubmitError('start', '任务启动失败，请重试')
      return
    }
    {
      const st = statusKey(startResp.data?.status) || TASK_STATUS.queued
      videoTasks.upsert({ task_id: taskId, status: st as any } as any)
      taskStore.setCurrent({ task_id: taskId, status: st } as any)
    }

    try {
      await auth.fetchMe()
    } catch {}
    resetVideo()
    void refreshTasks()
    closeSubmitDialog()
  } catch (e) {
    if (isAuthRequiredError(e)) return
    setSubmitError('create', e instanceof Error ? e.message : String(e))
  } finally {
    uploadBusy.value = false
  }
}

async function refreshTasks() {
  taskStore.stopPolling()
  videoTasks.stopAutoRefresh()

  if (!auth.token) return
  try {
    await videoTasks.fetchRecent(auth.token, 1, TASK_TYPES.removeWatermark_vid, videoTasks.listPageIndex, 'ms', undefined, true, {
      authExpiredMode: 'silent',
    })
  } catch (e) {
    if (isAuthRequiredError(e)) {
      videoTasks.reset()
      return
    }
  }
  void warmTaskThumbsAfterListLoaded()

  const hasActive = videoTasks.items.some((x: any) => {
    const tt = String(x?.task_type || '').trim()
    if (tt && tt !== TASK_TYPES.removeWatermark_vid) return false
    const subLen = Math.max(0, Math.round(Number(x?.sub_task_len || 0)) || 0)
    if (subLen !== 0) return false
    return isActiveTaskStatus(x?.status)
  })
  if (hasActive) videoTasks.startAutoRefresh(auth.token, 15_000, TASK_TYPES.removeWatermark_vid)
  else videoTasks.stopAutoRefresh()

}

async function refreshTasksWithToken(token: string) {
  taskStore.stopPolling()
  videoTasks.stopAutoRefresh()
  const t = String(token || '').trim()
  if (!t) return
  try {
    await videoTasks.fetchRecent(t, 1, TASK_TYPES.removeWatermark_vid, videoTasks.listPageIndex, 'ms', undefined, true, { authExpiredMode: 'silent' })
  } catch (e) {
    if (isAuthRequiredError(e)) {
      videoTasks.reset()
      return
    }
  }
  void warmTaskThumbsAfterListLoaded()
  const hasActive = videoTasks.items.some((x: any) => {
    const tt = String(x?.task_type || '').trim()
    if (tt && tt !== TASK_TYPES.removeWatermark_vid) return false
    const subLen = Math.max(0, Math.round(Number(x?.sub_task_len || 0)) || 0)
    if (subLen !== 0) return false
    return isActiveTaskStatus(x?.status)
  })
  if (hasActive) videoTasks.startAutoRefresh(t, 15_000, TASK_TYPES.removeWatermark_vid)
  else videoTasks.stopAutoRefresh()
}

const showPager = computed(() => videoTasks.listTotalPages > 1)
const canPrevPage = computed(() => videoTasks.listPageIndex > 0)
const canNextPage = computed(() => videoTasks.listPageIndex + 1 < videoTasks.listTotalPages)
const pageText = computed(() => `${videoTasks.listPageIndex + 1}/${videoTasks.listTotalPages}`)

async function gotoPage(idx: number) {
  if (!auth.token) return
  
  const i = Math.max(0, Math.round(Number(idx || 0)) || 0)
  if (i === videoTasks.listPageIndex) return
  taskStore.stopPolling()
  videoTasks.stopAutoRefresh()
  try {
    await videoTasks.fetchRecent(auth.token, 1, TASK_TYPES.removeWatermark_vid, i, 'ms', undefined, true, { authExpiredMode: 'silent' })
  } catch (e) {
    if (isAuthRequiredError(e)) {
      videoTasks.reset()
      return
    }
  }
  void warmTaskThumbsAfterListLoaded()
  const hasActive = videoTasks.items.some((x: any) => {
    const tt = String(x?.task_type || '').trim()
    if (tt && tt !== TASK_TYPES.removeWatermark_vid) return false
    const subLen = Math.max(0, Math.round(Number(x?.sub_task_len || 0)) || 0)
    if (subLen !== 0) return false
    return isActiveTaskStatus(x?.status)
  })
  if (hasActive) videoTasks.startAutoRefresh(auth.token, 15_000, TASK_TYPES.removeWatermark_vid)
  else videoTasks.stopAutoRefresh()
}

function prevPage() {
  void gotoPage(videoTasks.listPageIndex - 1)
}

function nextPage() {
  void gotoPage(videoTasks.listPageIndex + 1)
}

async function removeTask(taskId: string) {
  if (!auth.isLoggedIn || !auth.token) {
    videoTasks.remove(taskId)
    removeTaskThumbCache(taskId)
    return
  }
  try {
    const resp = await apiRequest<any>('/task/delete', {
      method: 'POST',
      token: auth.token,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ task_id: taskId }),
    } as any)
    if (Number(resp.code) !== 0) throw new Error(String(resp.message || '删除失败'))
    videoTasks.remove(taskId)
    removeTaskThumbCache(taskId)
    await videoTasks.fetchRecent(auth.token, 1, TASK_TYPES.removeWatermark_vid, videoTasks.listPageIndex, 'ms', undefined, true, { authExpiredMode: 'silent' })
    void warmTaskThumbsAfterListLoaded()
  } catch (e) {
    if (isAuthRequiredError(e)) videoTasks.reset()
  }
}

const canSubmit = computed(() => !!videoFile.value && !uploadBusy.value)
const refreshDisabled = computed(() => !auth.isLoggedIn || !auth.token || uploadBusy.value || videoTasks.refreshing || videoTasks.listing)
const visibleTasks = computed(() => {
  if (!auth.isLoggedIn) return []
  return videoTasks.items.filter((x: any) => x.task_type === TASK_TYPES.removeWatermark_vid && Math.max(0, Number(x?.sub_task_len || 0)) === 0)
})

const methodHint = computed(() => {
  if (method.value === 'sel_area') return '消除框选区域内的水印'
  if (method.value === 'ant_area') return '消除框选区域外的全部水印'
  return '智能识别并消除整个视频中的水印与文字，无需框选'
})

const submitPriceText = computed(() => {
  if (method.value === 'sel_area') return '8积分/每30秒视频'
  return '15积分/每30秒视频'
})

function statusKey(st: unknown) {
  return normalizeTaskStatus(st)
}

async function startExistingTask(taskId: string) {
  if (!ensureLoggedIn()) return
  if (!auth.token) return
  openSubmitDialog()
  submitStage.value = 'starting'
  submitUploadPct.value = 100
  uploadBusy.value = true
  try {
    const startResp = await apiRequest<StartTaskResponse>('/task/start', {
      method: 'POST',
      token: auth.token,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ task_id: taskId }),
    } as any)
    const startCode = Number(startResp.code)
    if (startCode === 3001 || startCode === 3002) {
      const required = typeof startResp.data?.credits_required === 'number' ? startResp.data.credits_required : 0
      closeSubmitDialog()
      void openRechargeModal('insufficient', required, String(startResp.message || '').trim())
      return
    }
    if (startCode !== 0) {
      setSubmitError('start', '任务启动失败，请重试')
      return
    }
    {
      const st = statusKey(startResp.data?.status) || TASK_STATUS.queued
      videoTasks.upsert({ task_id: taskId, status: st as any } as any)
      taskStore.setCurrent({ task_id: taskId, status: st } as any)
    }
    void refreshTasks()
    closeSubmitDialog()
  } catch (e) {
    setSubmitError('start', e instanceof Error ? e.message : String(e))
  } finally {
    uploadBusy.value = false
  }
}

async function cancelTask(taskId: string) {
  if (!ensureLoggedIn()) return
  if (!auth.token) return
  uploadBusy.value = true
  try {
    const resp = await apiRequest<any>('/task/stop', {
      method: 'POST',
      token: auth.token,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ task_id: taskId }),
    } as any)
    if (Number(resp.code) !== 0) throw new Error(String(resp.message || '取消失败'))
    {
      const st = statusKey((resp as any)?.data?.status) || TASK_STATUS.paused
      videoTasks.upsert({ task_id: taskId, status: st as any } as any)
      taskStore.setCurrent({ task_id: taskId, status: st } as any)
    }
  } catch (e) {
    openModal('取消失败', '我知道了', `<div class="miniCard"><h4>请稍后重试</h4><p>${e instanceof Error ? e.message : String(e)}</p></div>`)
  } finally {
    uploadBusy.value = false
  }
}

const downloadingTaskId = ref('')
function extractFilenameFromContentDisposition(value: string) {
  const raw = String(value || '').trim()
  if (!raw) return ''
  const parts = raw.split(';').map((x) => x.trim())
  const kv = new Map<string, string>()
  for (const p of parts) {
    const i = p.indexOf('=')
    if (i <= 0) continue
    const k = p.slice(0, i).trim().toLowerCase()
    const v = p.slice(i + 1).trim()
    kv.set(k, v)
  }
  const filenameStar = kv.get('filename*')
  if (filenameStar) {
    const v = filenameStar.replace(/^"(.*)"$/, '$1')
    const m = v.match(/^[^']*''(.+)$/)
    if (m?.[1]) {
      try {
        return decodeURIComponent(m[1])
      } catch {
        return m[1]
      }
    }
  }
  const filename = kv.get('filename')
  if (filename) return filename.replace(/^"(.*)"$/, '$1')
  return ''
}

function safeDownloadName(name: string) {
  const s = String(name || '').trim()
  if (!s) return ''
  return s.replace(/[\\/:*?"<>|\u0000-\u001F]/g, '_').slice(0, 160)
}

function rfc5987Encode(v: string) {
  return encodeURIComponent(v).replace(/[!'()*]/g, (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`)
}

function withContentDisposition(url: string, filename: string) {
  const href = String(url || '').trim()
  const name = String(filename || '').trim()
  if (!href || !name) return href
  try {
    const u = new URL(href)
    const disp = `attachment; filename*=UTF-8''${rfc5987Encode(name)}`
    u.searchParams.set('response-content-disposition', disp)
    return u.toString()
  } catch {
    return href
  }
}

function triggerBrowserDownload(url: string, filename: string) {
  const href = String(url || '').trim()
  if (!href) return
  const a = document.createElement('a')
  a.href = href
  a.rel = 'noopener noreferrer'
  a.download = safeDownloadName(filename)
  document.body.appendChild(a)
  a.click()
  a.remove()
}

function filenameFromDownloadUrl(url: string) {
  try {
    const u = new URL(url)
    const cd = u.searchParams.get('response-content-disposition') || ''
    const decoded = cd ? decodeURIComponent(cd) : ''
    const fromCd = safeDownloadName(extractFilenameFromContentDisposition(decoded))
    if (fromCd) return fromCd
    const last = u.pathname.split('/').filter(Boolean).pop() || ''
    return safeDownloadName(last)
  } catch {
    return ''
  }
}

async function downloadTask(taskId: string) {
  if (!ensureLoggedIn()) return
  if (!auth.token) return
  if (downloadingTaskId.value) return
  downloadingTaskId.value = taskId
  try {
    const resp = await apiRequest<any>(`/task/${encodeURIComponent(taskId)}`, { method: 'GET', token: auth.token })
    const data = assertOk(resp, '任务下载存在异常，请联系客服解决。') as any
    const url = String(data?.download_url || '').trim()
    if (!url) throw new Error('missing_download_url')
    videoTasks.upsert({ task_id: taskId, download_url: url } as any)
    const filename = filenameFromDownloadUrl(url)
    const href = withContentDisposition(url, filename)
    triggerBrowserDownload(href, filename)
  } catch (e) {
    if (isAuthRequiredError(e)) return
    openModal(
      '任务下载存在异常',
      '我知道了',
      `<div class="miniCard"><h4>请联系客服解决</h4><p>任务下载存在异常，请联系客服解决。</p></div>`,
    )
  } finally {
    downloadingTaskId.value = ''
  }
}

async function resubmitFromHistory(tid: string) {
  const t = videoTasks.items.find((x) => x.task_id === tid)
  if (!t?.source_video) return
  if (!ensureLoggedIn()) return
  uploadBusy.value = true
  try {
    const payload: any = { source_video: t.source_video, method: t.method || 'sel_area' }
    const rawName = String(t.title || '').trim()
    const taskName = rawName.replace(/\.[^./\\]+$/, '')
    if (taskName) payload.task_name = taskName
    if ((payload.method === 'sel_area' || payload.method === 'ant_area') && t.rect) payload.rect = t.rect
    const resp = await apiRequest<VideoRemoveResponse>('/video/removeWatermark', {
      method: 'POST',
      token: auth.token,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    } as any)
    const data = assertOk(resp, '提交失败') as any
    const taskId = String(data.task_id || '').trim()
    if (!taskId) throw new Error('提交失败')
    const localThumb = String(t.thumb_data_url || '').trim()
    if (localThumb) setTaskThumbToCache(taskId, localThumb)
    videoTasks.upsert({
      task_id: taskId,
      title: t.title,
      created_at: Date.now(),
      task_type: TASK_TYPES.removeWatermark_vid,
      status: String(data.status || 'created').toLowerCase() as any,
      thumb_data_url: t.thumb_data_url,
      source_video: t.source_video,
      method: payload.method,
      rect: payload.rect,
    })
    const startResp = await apiRequest<StartTaskResponse>('/task/start', {
      method: 'POST',
      token: auth.token,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ task_id: taskId }),
    } as any)
    const startCode = Number(startResp.code)
    if (startCode === 3001) {
      const required = typeof startResp.data?.credits_required === 'number' ? startResp.data.credits_required : 0
      void openRechargeModal('insufficient', required)
      return
    }
    if (startCode !== 0) throw new Error(String(startResp.message || '接入异常，请重试'))
    {
      const st = statusKey(startResp.data?.status) || TASK_STATUS.queued
      videoTasks.upsert({ task_id: taskId, status: st as any } as any)
      taskStore.setCurrent({ task_id: taskId, status: st } as any)
    }
    void refreshTasks()
  } catch (e) {
    openModal('重新提交失败', '我知道了', `<div class="miniCard"><h4>请稍后重试</h4><p>${e instanceof Error ? e.message : String(e)}</p></div>`)
  } finally {
    uploadBusy.value = false
  }
}

function formatDate(ts: number) {
  const d = new Date(ts)
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  return `${m}-${dd} ${hh}:${mm}`
}

async function onVideoLoadedMeta() {
  const vid = videoEl.value
  if (!vid) return
  videoW.value = Math.round(vid.videoWidth || 0)
  videoH.value = Math.round(vid.videoHeight || 0)
  videoDuration.value = Number.isFinite(vid.duration) ? vid.duration : 0
  initRect()
}

function onVideoEnded() {
  if (!isDemoVideo.value) return
  const v = videoEl.value
  if (!v) return
  if (!isMobileViewport.value) {
    try {
      v.pause()
      v.currentTime = 0
    } catch {}
    videoUrl.value = ''
    return
  }
  try {
    v.currentTime = 0
    void v.play()
  } catch {}
}

function openWeChatMiniProgram() {
  const url = 'weixin://dl/business/?appid=wx34b5be765618750e&path=pages/index/index&env_version=release'
  try {
    window.location.href = url
  } catch {}
}

watch(
  () => method.value,
  () => {
    if (method.value === 'sel_area' || method.value === 'ant_area') initRect()
  },
)

watch(
  () => [auth.isLoggedIn, auth.token] as const,
  ([logged, token]) => {
    if (!logged) {
      taskStore.stopPolling()
      taskStore.setCurrent(null)
      videoTasks.stopAutoRefresh()
      return
    }
    if (token) {
      void refreshTasksWithToken(token)
    }
  },
  { immediate: true },
)

watch(
  () => taskStore.current,
  (t) => {
    if (!auth.isLoggedIn) return
    if (!t?.task_id) return
    videoTasks.upsert({
      task_id: t.task_id,
      status: String(t.status || '').toLowerCase() as any,
      progress: typeof t.progress === 'number' ? t.progress : undefined,
      preview_url: t.preview_url || undefined,
      download_url: t.download_url || undefined,
      update_time: Date.now(),
      task_name: t.task_name || undefined,
    } as any)
  },
  { deep: true },
)

onMounted(async () => {
  window.addEventListener(AUTH_EXPIRED_EVENT, onAuthExpired as any)
  try {
    syncMobileViewport()
    window.addEventListener('resize', syncMobileViewport)
  } catch {}
  
  let proceed = true
  let tokenOverride = ''
  try {
    const raw = window.localStorage.getItem(AUTH_EXPIRED_STORAGE_KEY)
    if (raw) {
      window.localStorage.removeItem(AUTH_EXPIRED_STORAGE_KEY)
      proceed = false
      try {
        tokenOverride = await auth.refreshAccessToken()
      } catch {
        tokenOverride = ''
      }
      if (tokenOverride) proceed = true
    }
  } catch {}

  if (proceed) {
    videoTasks.init()
    if (tokenOverride) void refreshTasksWithToken(tokenOverride)
    else void refreshTasks()
  }

  void playDemoVideo()
})

onBeforeUnmount(() => {
  window.removeEventListener(AUTH_EXPIRED_EVENT, onAuthExpired as any)
  try {
    window.removeEventListener('resize', syncMobileViewport)
  } catch {}
  taskStore.stopPolling()
  videoTasks.stopAutoRefresh()
  revokeVideoUrl()
})
</script>

<template>
  <LandingBackground :enabled="!props.embed">
    <HomePageHeader v-if="!props.embed" />

    <main id="top">
    <section class="hero">
      <div class="container">
        <div class="heroStack">
          <h1 v-if="!props.embed" class="heroTitle">{{ landingSeo.h1 }}</h1>

          <section class="tool-grid" aria-label="Tool Area" id="workbench">
            <div class="card" aria-label="Preview and Operations">
              <div class="card-hd card-hd-no-divider card-hd-tight !hidden sm:!flex">
                <div class="flex items-center gap-4 min-w-0">
                  <button
                    class="inline-flex items-center gap-1.5 h-5 text-xs font-extrabold leading-none text-on-surface-variant hover:text-on-surface hover:underline underline-offset-4"
                    type="button"
                    aria-label="使用教程"
                    @click="openHowTo"
                  >
                    <span class="w-4 h-4 block shrink-0" aria-hidden="true" :style="tutorialIconMaskStyle"></span>
                    <span>教程</span>
                  </button>
                </div>
                <button
                  class="my-1.5 px-5 py-2.5 rounded-[var(--radius-btn)] bg-accent text-white font-black text-sm hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-default flex-none"
                  type="button"
                  :disabled="uploadBusy"
                  @click="onPickVideo"
                >
                  打开视频
                </button>
              </div>

              <div class="card-bd card-bd-tight">
                <div
                  class="video-frame"
                  ref="previewWrapEl"
                  aria-label="Video Placeholder"
                  @dragover="onDropzoneDragOver"
                  @drop="onDropzoneDrop"
                >
                  <div v-if="!videoUrl" class="video-placeholder" role="button" tabindex="0" aria-label="上传视频" @click="onPickVideo">
                    <svg class="cameraIcon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <g transform="translate(0 -0.5)">
                        <path
                          d="M4.5 8.5A2.5 2.5 0 0 1 7 6h7a2.5 2.5 0 0 1 2.5 2.5v7A2.5 2.5 0 0 1 14 18H7a2.5 2.5 0 0 1-2.5-2.5v-7Z"
                          stroke="currentColor"
                          stroke-width="2.2"
                          stroke-linejoin="round"
                        />
                        <path d="M16.5 10.2 20 8v8l-3.5-2.2v-3.6Z" stroke="currentColor" stroke-width="2.2" stroke-linejoin="round" />
                        <path d="M10 9.3v5.4l5-2.7-5-2.7Z" fill="currentColor" opacity="0.95" />
                      </g>
                    </svg>
                    <div class="video-placeholder-text">
                      <div class="video-placeholder-title">将视频拖放此打开</div>
                      <div class="video-placeholder-sub">支持MP4/MOV格式，最大1080P，最长15分钟</div>
                    </div>
                  </div>

                  <template v-else>
                    <video
                      ref="videoEl"
                      class="videoPreview"
                      :src="videoUrl"
                      :muted="isDemoVideo"
                      :autoplay="isDemoVideo"
                      :loop="isDemoVideo && isMobileViewport"
                      :controls="!isDemoVideo"
                      :disablepictureinpicture="isDemoVideo"
                      controlslist="nodownload noplaybackrate noremoteplayback"
                      playsinline
                      @loadedmetadata="onVideoLoadedMeta"
                      @ended="onVideoEnded"
                    ></video>
                    <div v-if="method === 'sel_area' && rectAtLimit" class="absolute left-0 right-0 top-2 px-3 flex justify-center pointer-events-none">
                      <div class="px-3 py-1.5 rounded-xl bg-black/55 text-error text-xs font-extrabold">
                        已达到最大框选范围，如仍无法覆盖所有水印，请使用【全屏消除】模式
                      </div>
                    </div>
                    <div
                      v-if="(method === 'sel_area' || method === 'ant_area') && !(videoUrl === props.demoVideoUrl && !videoFile)"
                      class="vg-rect-overlay"
                      :class="method === 'sel_area' && rectAtLimit ? 'vg-rect-overlay--error' : ''"
                      :style="rectStyle"
                      @pointerdown="onRectPointerDown"
                      @pointermove="onRectPointerMove"
                      @pointerup="onRectPointerUp"
                      @pointercancel="onRectPointerUp"
                    >
                      <span class="vg-rect-handle nw" data-handle="nw" aria-hidden="true"></span>
                      <span class="vg-rect-handle ne" data-handle="ne" aria-hidden="true"></span>
                      <span class="vg-rect-handle sw" data-handle="sw" aria-hidden="true"></span>
                      <span class="vg-rect-handle se" data-handle="se" aria-hidden="true"></span>
                    </div>
                  </template>
                </div>

                <div v-if="isDemoVideo" class="mt-4 sm:hidden">
                  <button class="btn btn-primary w-full" type="button" @click="openWeChatMiniProgram">开始视频去水印</button>
                </div>

                <div class="hint !hidden sm:!block">{{ methodHint }}</div>

                <div class="video-toolbar !hidden sm:!flex" aria-label="Video Controls">
                  <div class="seg seg-toggle" role="group" aria-label="Mode">
                    <button class="chip" type="button" :aria-pressed="method === 'sel_area'" @click="method = 'sel_area'">框内消除</button>
                    <button class="chip" type="button" :aria-pressed="method === 'ant_area'" @click="method = 'ant_area'">框外消除</button>
                    <button class="chip" type="button" :aria-pressed="method === 'all_area'" @click="method = 'all_area'">全屏消除</button>
                  </div>
                  <button class="btn btn-primary btn-submit" type="button" :disabled="!canSubmit" @click="submit">
                    <template v-if="uploadBusy">正在提交</template>
                    <template v-else>
                      <span>提交处理</span>
                      <span class="submitPrice">{{ submitPriceText }}</span>
                    </template>
                  </button>
                </div>
              </div>
            </div>

            <div class="card hidden sm:block" aria-label="Task List">
              <div class="card-hd card-hd-no-divider">
                <div class="card-title">任务列表（近1天）</div>
                <div class="flex items-center gap-1">
                  <button
                    class="w-9 h-9 rounded-xl text-on-surface hover:bg-white/6 transition-colors grid place-items-center disabled:opacity-50 disabled:cursor-default"
                    type="button"
                    aria-label="刷新"
                    :disabled="refreshDisabled"
                    @click="refreshTasks"
                  >
                    <IconRefresh class="w-5 h-5" />
                  </button>
                  <div v-if="showPager" class="flex items-center gap-0.5">
                    <button
                      class="w-9 h-9 rounded-xl text-on-surface hover:bg-white/6 transition-colors grid place-items-center disabled:opacity-50 disabled:cursor-default"
                      type="button"
                      aria-label="上一页"
                      :disabled="refreshDisabled || !canPrevPage"
                      @click="prevPage"
                    >
                      <IconChevronLeft class="w-5 h-5" />
                    </button>
                    <div class="text-xs font-extrabold text-on-surface-variant tabular-nums min-w-[40px] text-center">{{ pageText }}</div>
                    <button
                      class="w-9 h-9 rounded-xl text-on-surface hover:bg-white/6 transition-colors grid place-items-center disabled:opacity-50 disabled:cursor-default"
                      type="button"
                      aria-label="下一页"
                      :disabled="refreshDisabled || !canNextPage"
                      @click="nextPage"
                    >
                      <IconChevronRight class="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>

              <div class="card-bd task-list-bd">
                <div v-if="!visibleTasks.length" class="hint">暂无任务，提交后会在这里显示。</div>
                <div v-else class="tasks" role="list">
                  <div class="task" v-for="t in visibleTasks" :key="t.task_id" role="listitem" tabindex="0">
                    <div class="thumb">
                      <img :src="taskThumbSrc(t) || defaultVideoThumbDataUrl()" alt="" />
                    </div>
                    <div class="task-main">
                      <div>
                        <div class="task-title">{{ t.task_name || t.title }}</div>
                        <div class="task-meta">
                          <span>{{ taskMetaText(t) }}</span>
                        </div>
                      </div>
                      <div class="task-actions">
                        <template v-if="statusKey(t.status) === TASK_STATUS.created">
                          <button class="btn btn-icon" type="button" aria-label="启动" title="启动" :disabled="uploadBusy" @click="startExistingTask(t.task_id)">
                            <IconPlay />
                          </button>
                          <button class="btn btn-icon" type="button" aria-label="删除" title="删除" :disabled="uploadBusy" @click="removeTask(t.task_id)">
                            <IconTrash />
                          </button>
                        </template>
                        <template v-else-if="statusKey(t.status) === TASK_STATUS.queued">
                          <button class="btn btn-icon" type="button" aria-label="取消" title="取消" :disabled="uploadBusy" @click="cancelTask(t.task_id)">
                            <IconClose />
                          </button>
                        </template>
                        <template v-else-if="statusKey(t.status) === TASK_STATUS.processing">
                        </template>
                        <template v-else-if="statusKey(t.status) === TASK_STATUS.success">
                          <button
                            class="btn btn-icon"
                            type="button"
                            aria-label="下载"
                            title="下载"
                            :disabled="uploadBusy || downloadingTaskId === t.task_id"
                            @click="downloadTask(t.task_id)"
                          >
                            <IconDownload />
                          </button>
                          <button class="btn btn-icon" type="button" aria-label="删除" title="删除" :disabled="uploadBusy" @click="removeTask(t.task_id)">
                            <IconTrash />
                          </button>
                        </template>
                        <template v-else-if="statusKey(t.status) === TASK_STATUS.failed || statusKey(t.status) === TASK_STATUS.paused">
                          <button class="btn btn-icon" type="button" aria-label="启动" title="启动" :disabled="uploadBusy || !t.source_video" @click="resubmitFromHistory(t.task_id)">
                            <IconPlay />
                          </button>
                          <button class="btn btn-icon" type="button" aria-label="删除" title="删除" :disabled="uploadBusy" @click="removeTask(t.task_id)">
                            <IconTrash />
                          </button>
                        </template>
                        <template v-else>
                          <button class="btn btn-icon" type="button" aria-label="删除" title="删除" :disabled="uploadBusy" @click="removeTask(t.task_id)">
                            <IconTrash />
                          </button>
                        </template>
                      </div>
                    </div>
                    <div class="task-status">
                      <TaskStatusTag :status="t.status" />
                      <div v-if="statusKey(t.status) === TASK_STATUS.queued" class="task-hint">{{ queueHintText(t) }}</div>
                      <div v-else-if="statusKey(t.status) === TASK_STATUS.processing" class="task-hint">{{ processingHintText(t) }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </section>

    <template v-if="!props.embed">
    <section v-if="recommendations.length" class="section" id="recommend">
      <div class="container">
        <div class="sectionCard">
          <div class="recommendGrid" aria-label="产品推荐">
            <div class="recommendCell" v-for="(r, idx) in recommendations" :key="idx">
              <div class="recommendIcon" aria-hidden="true">
                <img class="recommendIconImg" :src="withBase(r.icon)" alt="" loading="lazy" />
              </div>
              <div class="recommendBody">
                <div class="recommendName">{{ r.name }}</div>
                <div class="recommendDesc">{{ r.desc }}</div>
                <a class="ghost mini recommendCta" :href="resolveRecommendHref(r.href)">
                  <span>去使用</span>
                  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M9 6l6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section v-if="sectionEnabled('howTo')" class="section" id="howto">
      <div class="container">
        <div class="sectionCard">
          <article class="seo">
            <h2>{{ props.howToTitle || '如何消除视频上的水印' }}</h2>
            <div class="howtoGrid" aria-label="使用步骤">
              <div class="howtoCell" v-for="(s, idx) in landingSeo.howto" :key="idx">
                <div class="howtoIcon" aria-hidden="true">
                  <svg viewBox="0 0 44 44" fill="none">
                    <circle cx="22" cy="22" r="20" stroke="currentColor" stroke-width="2" opacity="0.22" />
                    <circle cx="22" cy="22" r="18" stroke="currentColor" stroke-width="2" opacity="0.10" />
                    <text x="22" y="28" text-anchor="middle" font-size="18" font-weight="800" fill="currentColor">{{ Number(idx) + 1 }}</text>
                  </svg>
                </div>
                <div class="howtoName">{{ s.title }}</div>
                <div class="howtoDesc">{{ s.desc }}</div>
              </div>
            </div>
          </article>
        </div>
      </div>
    </section>

    <section v-if="sectionEnabled('api') && landingSeo.apiTitle && landingSeo.apiPoints?.length" class="section api-section" id="api">
      <div class="container">
        <div class="sectionCard">
          <article class="seo">
            <div class="code-wrap">
              <div class="code-pane">
                <div class="head"><span class="dots"><i></i><i></i><i></i></span> Sample Code</div>
                <pre># 提交视频去水印任务
curl -X POST "https://api.wuhenai.com/v2/video_removal" \
  -H "Authorization: Bearer $ACCESS_TOKEN" \
  -H "Content-Type: application/json" \
  -d '{
    "video_url": "https://cdn.example.com/in.mp4",
    "model": "video_removal_std",
    "method": "all_area",
    "upload_url": "https://oss.yourdomain.com/in.mp4",
    "upload_headers": {"Content-Type": "video/mp4"}
  }'

# 查询处理进度
curl "https://api.wuhenai.com/v1/tasks/{task_id}" \
  -H "Authorization: Bearer $ACCESS_TOKEN"</pre>
              </div>
              <div class="api-points">
                <h2>{{ landingSeo.apiTitle }}</h2>
                <div class="api-point" v-for="(p, idx) in landingSeo.apiPoints.slice(0, 3)" :key="idx">
                  <span class="ico" aria-hidden="true">
                    <svg v-if="idx === 0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M13 2L3 14h7l-1 8 10-12h-7l1-8z"/></svg>
                    <svg v-else-if="idx === 1" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3l7 4v6c0 4.4-3 7.5-7 9-4-1.5-7-4.6-7-9V7l7-4z"/><path d="M9 12l2 2 4-4"/></svg>
                    <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 7l4 4-4 4M9 7h12M9 15h8"/></svg>
                  </span>
                  <div>
                    <b>{{ p.title }}</b>
                    <p>{{ p.desc }}</p>
                  </div>
                </div>
                <a v-if="landingSeo.apiHref" class="cta api-cta" :href="resolveRecommendHref(landingSeo.apiHref)">
                  <span>{{ landingSeo.apiLinkText || '查看 API 文档' }}</span>
                  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </a>
              </div>
            </div>
          </article>
        </div>
      </div>
    </section>

    <section v-if="sectionEnabled('useCases')" class="section" id="where">
      <div class="container">
        <div class="sectionCard">
          <article class="seo">
            <h2>{{ landingSeo.whereTitle }}</h2>
            <div class="whereGrid" aria-label="适用场景">
              <div class="whereCell" v-for="(w, idx) in landingSeo.whereItems.slice(0, 12)" :key="idx">
                <h3>{{ w.title }}</h3>
                <div class="whereThumb" aria-hidden="true">
                  <img class="whereImg" :src="whereImageSrc(Number(idx))" :alt="w.title" loading="lazy" />
                </div>
              </div>
            </div>
          </article>
        </div>
      </div>
    </section>

    <section v-if="sectionEnabled('benefits')" class="section" id="why-erase">
      <div class="container">
        <div class="sectionCard">
          <article class="seo">
            <template v-if="landingSeo.whyNeedTitle && landingSeo.whyNeedItems?.length">
              <h2>{{ landingSeo.whyNeedTitle }}</h2>
              <div class="whyNeedGrid">
                <div class="whyNeedCard" v-for="(x, idx) in landingSeo.whyNeedItems" :key="idx">
                  <img class="whyNeedImg" :src="whyNeedImageSrc(Number(idx))" :alt="x.title" loading="lazy" />
                  <div class="whyNeedOverlay">
                    <h3>{{ x.title }}</h3>
                    <p>{{ x.desc }}</p>
                  </div>
                </div>
              </div>
            </template>
          </article>
        </div>
      </div>
    </section>

    <section v-if="sectionEnabled('features') && landingSeo.featuresTitle && featureItems.length" class="section" id="features">
      <div class="container">
        <div class="sectionCard">
          <article class="seo">
            <h2>{{ landingSeo.featuresTitle }}</h2>
            <div class="featuresList" aria-label="功能亮点">
              <div class="featureRow" v-for="(f, idx) in featureItems" :key="idx">
                <div class="featureMedia" aria-hidden="true">
                  <img class="featureImg" :src="withBase(f.image)" :alt="f.title" loading="lazy" />
                </div>
                <div class="featureBody">
                  <h3>{{ f.title }}</h3>
                  <p>{{ f.desc }}</p>
                </div>
              </div>
            </div>
          </article>
        </div>
      </div>
    </section>

    <section v-if="sectionEnabled('whyChoose')" class="section" id="why-choose">
      <div class="container">
        <div class="sectionCard">
          <article class="seo">
            <h2>{{ landingSeo.whyTitle }}</h2>
            <div class="whyChooseGrid" aria-label="为什么选择">
              <div class="whyChooseCard" v-for="(w, idx) in whyChooseItems" :key="idx">
                <img class="whyChooseImg" :src="withBase(w.image)" :alt="w.title" loading="lazy" />
                <h3>{{ w.title }}</h3>
                <p>{{ w.desc }}</p>
              </div>
            </div>
          </article>
        </div>
      </div>
    </section>

    <section v-if="sectionEnabled('testimonials')" class="section" id="quotes">
      <div class="container">
        <div class="sectionCard">
          <article class="seo">
            <template v-if="landingSeo.quoteTitle && landingSeo.quoteItems?.length">
              <h2>{{ landingSeo.quoteTitle }}</h2>
              <div class="letterGrid" aria-label="用户推荐信">
                <article class="letterCard" v-for="(q, idx) in landingSeo.quoteItems.slice(0, 4)" :key="idx">
                  <h3 class="letterTitle">{{ q.title }}</h3>
                  <div class="letterStars" aria-hidden="true">★★★★★</div>
                  <p class="letterText">{{ q.text }}</p>
                  <footer class="letterPerson" aria-label="推荐人信息">
                    <img class="letterAvatar" :src="withBase(`/assets/quote-avatar-${Number(idx) + 1}.jpg`)" :alt="`${q.name} 头像`" loading="lazy" />
                    <div class="letterMeta">
                      <div class="letterName">{{ q.name }}</div>
                      <div class="letterJob">{{ q.job }}</div>
                    </div>
                  </footer>
                </article>
              </div>
            </template>
          </article>
        </div>
      </div>
    </section>

    <section v-if="sectionEnabled('faq')" class="section faq" id="faq">
      <div class="container">
        <div class="sec-head">
          <h2>{{ landingSeo.faqTitle || '常见问题' }}</h2>
        </div>
        <div aria-label="常见问题">
          <details v-for="(f, idx) in landingSeo.faq" :key="idx">
            <summary>
              <span>{{ f.question }}</span>
              <span class="plus">+</span>
            </summary>
            <div class="a">{{ f.answer }}</div>
          </details>
        </div>
      </div>
    </section>

    <section v-if="sectionEnabled('contact')" class="section" id="contact">
      <div class="container" id="cta">
        <div class="cta-band">
          <h2>欢迎使用无痕AI{{ props.breadcrumbName }}</h2>
          <p>新用户赠送免费试用积分，有问题可随时联系客服</p>
          <a class="btn btn-primary" href="#">开始在线{{ props.breadcrumbName }}｜免费试用
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
          </a>
          <div class="cta-qr">
            <div class="qr-wrap" aria-label="客服微信二维码">
              <img :src="withBase('/assets/custom-service-qr.webp')" alt="客服微信二维码" loading="lazy" />
            </div>
            <span class="qr-tag">在线时间：09:00 - 22:00</span>
          </div>
        </div>
      </div>
    </section>
    </template>
    <HomePageFooter v-if="!props.embed" />
  </main>

  <input
    ref="filePicker"
    type="file"
    accept="video/*"
    style="display: none"
    @change="
      (e) => {
        const f = (e.target as HTMLInputElement).files?.[0]
        if (f) handleFileSelected(f)
      }
    "
  />

  <TaskSubmitProgressDialog
    :open="submitOpen"
    :stage="submitStage"
    :upload-pct="submitUploadPct"
    :error-step="submitErrorStep"
    :error-text="submitErrorText"
    aria-label="视频提交中"
    @update:open="
      (v) => {
        if (v) submitOpen = true
        else closeSubmitDialog()
      }
    "
    @ack="closeSubmitDialog"
  />

  <MessageDialog v-model:open="warnOpen" title="提示" :message="warnText" variant="info" primary-text="我知道了" @primary="closeWarn" @close="closeWarn" />

  <div v-if="modalOpen" class="modalOverlay" role="dialog" aria-modal="true" aria-label="提示" style="display: flex" @click.self="closeModal">
    <div class="modal">
      <div class="modalHeader">
        <strong>{{ modalTitle }}</strong>
        <button class="iconBtn" type="button" aria-label="关闭" @click="closeModal">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <path d="M18 6 6 18"></path>
            <path d="M6 6l12 12"></path>
          </svg>
        </button>
      </div>
      <div class="modalBody">
        <div :innerHTML="modalBodyHtml"></div>
        <div class="btnRow btnRowCenter" style="margin-top: 14px">
          <button
            class="ghost"
            type="button"
            @click="
              () => {
                const fn = modalPrimaryAction
                closeModal()
                if (fn) fn()
              }
            "
          >
            {{ modalPrimaryText }}
          </button>
        </div>
      </div>
    </div>
  </div>
  </LandingBackground>
</template>

<style src="../styles/videoRectOverlay.css"></style>
<style scoped>
.api-section .code-wrap {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 0;
  min-height: 360px;
  border: 1px solid var(--line-strong, rgba(255, 255, 255, 0.18));
  border-radius: var(--radius, 16px);
  overflow: hidden;
  background: var(--bg-1, rgba(255, 255, 255, 0.1));
  box-shadow: var(--shadow-card, 0 12px 28px rgba(0, 0, 0, 0.28));
}

.api-section .code-pane {
  padding: 0;
  background: rgba(0, 0, 0, 0.42);
  min-width: 0;
}

.api-section .code-pane .head {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
  font-family: var(--font-mono, ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace);
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
}

.api-section .code-pane .head .dots {
  display: flex;
  gap: 5px;
  margin-right: 8px;
}

.api-section .code-pane .head .dots i {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
}

.api-section .code-pane .head .dots i:nth-child(1) {
  background: #ff5a5a;
}

.api-section .code-pane .head .dots i:nth-child(2) {
  background: #f4d35e;
}

.api-section .code-pane .head .dots i:nth-child(3) {
  background: #4ade80;
}

.api-section .code-pane pre {
  padding: 22px 24px;
  overflow-x: hidden;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  font-family: var(--font-mono, ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace);
  font-size: 13px;
  line-height: 1.75;
  color: rgba(255, 255, 255, 0.86);
  margin: 0;
}

.api-section .api-points {
  padding: 28px 32px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 22px;
  min-width: 0;
}

.api-section .api-points h2 {
  font-size: var(--seo-h2-size, 32px);
  font-weight: var(--seo-h2-weight, 700);
  margin: 0 0 18px;
  text-align: left;
  color: rgba(255, 255, 255, 0.92);
}

.api-section .api-point {
  display: flex;
  gap: 14px;
  align-items: flex-start;
}

.api-section .api-point .ico {
  width: 34px;
  height: 34px;
  flex: none;
  border-radius: 8px;
  display: grid;
  place-items: center;
  background: var(--accent-dim, rgba(0, 139, 255, 0.22));
  color: rgba(255, 255, 255, 0.92);
}

.api-section .api-point .ico svg {
  width: 17px;
  height: 17px;
}

.api-section .api-point b {
  font-size: 15px;
  display: block;
  color: rgba(255, 255, 255, 0.92);
}

.api-section .api-point p {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.7);
  margin-top: 2px;
  margin-bottom: 0;
}

.api-section .api-cta {
  align-self: flex-start;
  text-decoration: none;
  border-radius: var(--radius-sm, 12px);
  padding: 12px 18px;
}

.api-section .api-cta svg {
  width: 18px;
  height: 18px;
}

@media (max-width: 960px) {
  .api-section .code-wrap {
    grid-template-columns: 1fr;
  }
}
</style>
